'use client';
import { Box } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import {
  DataGridPro,
  GridColDef,
  GridColumnVisibilityModel,
  GridDensity,
  GRID_DETAIL_PANEL_TOGGLE_FIELD,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  GridSortModel,
} from '@mui/x-data-grid-pro';

import { useEffect, useState, useCallback, useMemo } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useBackend } from '@/hooks/useBackend';
import { EMBEDDED_QUERY_PARAM, isEmbeddedQueryValue } from '@/lib/embeddedQuery';
import {
  useDataPackageFilterStore,
  loadStoredColumnVisibility,
  COLUMN_VISIBILITY_STORAGE_KEY,
} from '@/stores/dataPackageFilterStore';
import { scopedLocalStorage } from '@/lib/browser/scopedStorage';
import { StatusDto } from '@/api/ApiClient';
import { stripDoiPrefix, toDoiHref } from '@/lib/utils/doiLinks';

// Storage key for sort model
const DATAPACKAGE_SORT_MODEL_STORAGE_KEY = 'dataPackageGridSortModel';

// Helper function to load sort model from localStorage
const loadStoredDataPackageSortModel = (): GridSortModel => {
  try {
    const stored = scopedLocalStorage.getItem(DATAPACKAGE_SORT_MODEL_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Validate that it's an array and has the expected structure
      if (
        Array.isArray(parsed) &&
        parsed.every(
          (item) =>
            typeof item === 'object' && typeof item.field === 'string' && (item.sort === 'asc' || item.sort === 'desc')
        )
      ) {
        return parsed;
      }
    }
  } catch (error) {
    log.warn('Failed to load stored data package sort model:', error);
  }
  // Return default sort model
  return [{ field: 'label', sort: 'asc' }];
};
import { log } from '@/lib/utils/logger';
import { dataGridStyle } from '@/styles/tableStyles';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { OptionKey } from '@/lib/options/optionTypes';
import { useSystemConfig } from '@/hooks/useSystemConfig';
import { DataGridToolbar } from '@/components/tables/DataGridToolbar';
import { useUserResolution } from '@/lib/options/useUserResolution';
import { removeLeadingTrailingBrTags } from '@/lib/utils/textUtils';
import { useGridSearchErrorHandler } from '@/hooks/useGridSearchErrorHandler';
import { consumeNextSearchUserInitiated } from '@/stores/searchUiStore';

const OPTION_KEYS: OptionKey[] = ['synthesisTechniques', 'characterizationTechniques', 'materials', 'staff'];

export type DataPackageRowType = {
  id: number;
  label: string;
  prePublishId?: string;
  doi?: string;
  projectLabel?: string;
  publicationDoi?: string;
  availableData: string;
  type: string;
  lastChangedDate: string;
  description?: string;
  nbSamples: number;
  nbActivities: number;
  isSnapshot: boolean;
  status: string;
  isPublic: boolean;
};

export const DataPackageGrid = ({ onRefresh }: { onRefresh?: (refreshFn: () => void) => void } = {}) => {
  const backend = useBackend();
  const searchParams = useSearchParams();
  const isEmbedded = isEmbeddedQueryValue(searchParams.get(EMBEDDED_QUERY_PARAM));
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { options, loading: optionsLoading } = useSelectOptions(OPTION_KEYS);
  const systemConfig = useSystemConfig();
  const { resolveUser } = useUserResolution();
  const { handleGridSearchError } = useGridSearchErrorHandler('data packages');

  const filters = useDataPackageFilterStore((state) => state.filters);
  const paginationModel = useDataPackageFilterStore((state) => state.paginationModel);
  const setPaginationModel = useDataPackageFilterStore((state) => state.setPaginationModel);
  const setIsSearching = useDataPackageFilterStore((state) => state.setIsSearching);

  const [rows, setRows] = useState<DataPackageRowType[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalRows, setTotalRows] = useState(0);
  const [sortModel, setSortModel] = useState<GridSortModel>(loadStoredDataPackageSortModel());

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(loadStoredColumnVisibility());

  const [density, setDensity] = useState<GridDensity>('standard');

  const handleColumnVisibilityChange = (newModel: GridColumnVisibilityModel) => {
    setColumnVisibilityModel(newModel);
    scopedLocalStorage.setItem(COLUMN_VISIBILITY_STORAGE_KEY, JSON.stringify(newModel));
  };

  // Hide Project on narrow viewports; not persisted (user prefs apply again when wider).
  const effectiveColumnVisibilityModel = useMemo(() => {
    if (!isSmallScreen) {
      return columnVisibilityModel;
    }
    return { ...columnVisibilityModel, projectLabel: false };
  }, [columnVisibilityModel, isSmallScreen]);

  const handleSortModelChange = (newModel: GridSortModel) => {
    setSortModel(newModel);
    scopedLocalStorage.setItem(DATAPACKAGE_SORT_MODEL_STORAGE_KEY, JSON.stringify(newModel));
  };

  // Expose refresh function to parent
  const refreshData = useCallback(() => {
    // Trigger a re-fetch by updating a dependency
    setRows([]);
    setLoading(true);
  }, []);

  // Expose refresh function to parent component
  useEffect(() => {
    if (onRefresh) {
      onRefresh(refreshData);
    }
  }, [onRefresh, refreshData]);

  useEffect(() => {
    // Don't fetch if options are still loading
    if (optionsLoading) {
      log.debug('[DataPackageGrid] Options still loading, skipping fetch');
      return;
    }

    // Check if we have all the required options loaded
    const hasAllOptions =
      options.synthesisTechniques && options.characterizationTechniques && options.materials && options.staff;

    if (!hasAllOptions) {
      log.debug('[DataPackageGrid] Missing required options, skipping fetch:', {
        optionsLoading,
        hasAllOptions,
        synthesisTechniques: !!options.synthesisTechniques,
        characterizationTechniques: !!options.characterizationTechniques,
        materials: !!options.materials,
        staff: !!options.staff,
      });
      return;
    }

    log.debug('[DataPackageGrid] All options loaded, fetching data');
    const fetchDataPackages = async () => {
      const userInitiatedSearch = consumeNextSearchUserInitiated();
      try {
        setIsSearching(true);
        setLoading(true);
        const start = paginationModel.page * paginationModel.pageSize;
        const length = paginationModel.pageSize;
        const draw = paginationModel.page + 1;
        const sortColumn = sortModel[0]?.field || 'label';
        const sortOrder = sortModel[0]?.sort || 'asc';

        // Convert filters to search criteria format
        const criteria = {
          elements: filters.elements,
          materials: filters.materials,
          searchText: filters.searchText,
          dataType: filters.dataType,
          synTechniqueId: filters.synTechniqueId,
          charTechniqueId: filters.charTechniqueId,
          topLevelOrPublicOnly: filters.topLevelOrPublicOnly,
          publicOnly: filters.publicOnly,
        };

        const dataPackageList = await backend.data.search(sortColumn, sortOrder, start, length, draw, criteria);

        const formattedRows: DataPackageRowType[] = dataPackageList.data.map((dataPackage) => ({
          id: dataPackage.id!,
          label: dataPackage.label ?? dataPackage.id?.toString() ?? 'N/A',
          doi: dataPackage.doi,
          projectLabel: dataPackage.projectLabel,
          publicationDoi: dataPackage.publicationDoi,
          availableData: formatAvailableData(dataPackage.nbSamples ?? 0, dataPackage.nbActivities ?? 0),
          type: formatType(
            dataPackage.isSnapshot ?? false,
            dataPackage.status ?? 'N/A',
            dataPackage.isPublished ?? false
          ),
          lastChangedDate: dataPackage.lastChangedDate
            ? new Date(dataPackage.lastChangedDate).toLocaleDateString()
            : 'N/A',
          description: dataPackage.description,
          nbSamples: dataPackage.nbSamples ?? 0,
          nbActivities: dataPackage.nbActivities ?? 0,
          isSnapshot: dataPackage.isSnapshot ?? false,
          status: dataPackage.status ?? 'N/A',
          isPublic: dataPackage.isPublished ?? false,
        }));

        setRows(formattedRows ?? []);
        setTotalRows(dataPackageList?.recordsFiltered ?? 0);
      } catch (error) {
        log.error('[DataPackageGrid] Error fetching data packages:', error);
        handleGridSearchError(error, { userInitiatedSearch });
      } finally {
        setLoading(false);
        setIsSearching(false);
      }
    };

    fetchDataPackages();
  }, [
    backend,
    paginationModel,
    sortModel,
    filters,
    resolveUser,
    optionsLoading,
    options,
    setIsSearching,
    handleGridSearchError,
  ]);

  // Don't render the grid until options are loaded to avoid partial rendering
  if (optionsLoading) {
    return null;
  }

  const renderDoiCell = (doiValue?: string) => {
    if (!doiValue) return '';

    const href = toDoiHref(doiValue);
    const displayDoi = stripDoiPrefix(doiValue);
    if (isSmallScreen) {
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Open DOI ${displayDoi}`}
          title={displayDoi}
          style={{ color: '#1976d2', display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}
        >
          <i className="ai ai-doi ai-lg" aria-hidden="true" />
        </a>
      );
    }

    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: '#1976d2', textDecoration: 'underline' }}
      >
        {displayDoi}
      </a>
    );
  };

  const columns: GridColDef[] = [
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
    },
    {
      field: 'label',
      headerName: 'Label',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        const row = params.row as DataPackageRowType;
        return (
          <Link
            href={`${systemConfig?.basePath}/data/${encodeURIComponent(row.doi ?? row.prePublishId ?? row.id.toString())}`}
            passHref
            {...(isEmbedded ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
          >
            <span
              style={{
                color: '#1976d2',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              {params.value}
            </span>
          </Link>
        );
      },
    },
    {
      field: 'doi',
      headerName: isSmallScreen ? 'DOI' : 'Dataset DOI',
      flex: isSmallScreen ? 0 : 1,
      width: isSmallScreen ? 80 : undefined,
      minWidth: isSmallScreen ? 80 : 150,
      maxWidth: isSmallScreen ? 80 : undefined,
      renderCell: (params) => renderDoiCell(params.value),
    },
    {
      field: 'projectLabel',
      headerName: 'Project',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => params.value || 'N/A',
    },
    {
      field: 'publicationDoi',
      headerName: isSmallScreen ? 'Pub' : 'Publication DOI',
      flex: isSmallScreen ? 0 : 1,
      width: isSmallScreen ? 72 : undefined,
      minWidth: isSmallScreen ? 72 : 150,
      maxWidth: isSmallScreen ? 72 : undefined,
      renderCell: (params) => renderDoiCell(params.value),
    },
    {
      field: 'availableData',
      headerName: 'Available Data',
      flex: 1,
      minWidth: 150,
      sortable: false,
    },
    {
      field: 'type',
      headerName: 'Type',
      flex: 1,
      minWidth: 150,
      sortable: false,
    },
    {
      field: 'lastChangedDate',
      headerName: 'Date',
      flex: 1,
      minWidth: 150,
    },
  ];

  return (
    <DataGridPro
      rows={rows}
      columns={columns}
      loading={loading}
      rowCount={totalRows}
      columnVisibilityModel={effectiveColumnVisibilityModel}
      onColumnVisibilityModelChange={handleColumnVisibilityChange}
      pagination
      paginationMode="server"
      paginationModel={paginationModel}
      sortingMode="server"
      sortModel={sortModel}
      onSortModelChange={handleSortModelChange}
      onPaginationModelChange={setPaginationModel}
      pageSizeOptions={[25, 50, 100]}
      disableRowSelectionOnClick
      disableColumnMenu
      showToolbar
      density={density}
      onDensityChange={(newDensity) => setDensity(newDensity)}
      slots={{ toolbar: DataGridToolbar }}
      getRowHeight={() => 'auto'}
      initialState={{
        pinnedColumns: { left: [GRID_DETAIL_PANEL_TOGGLE_FIELD, 'label'] },
      }}
      getDetailPanelContent={({ row }) => {
        // Show description if available
        const description = removeLeadingTrailingBrTags(row.description || '');
        const isValidDescription = description && description !== '<br>';

        if (!isValidDescription) {
          return null; // Don't render the panel
        }

        return (
          <Box
            sx={{
              bgcolor: '#f9f9f9',
              border: '1px solid #ccc',
              borderRadius: 2,
              boxShadow: 2,
              m: 2,
              p: 2,
            }}
          >
            <Box sx={{ fontWeight: 'bold', fontSize: '1rem', mb: 1 }}>Description:</Box>
            <Box
              sx={{
                whiteSpace: 'pre-wrap',
                fontSize: '0.95rem',
                color: '#333',
              }}
              dangerouslySetInnerHTML={{ __html: description }}
            />
          </Box>
        );
      }}
      getDetailPanelHeight={({ row }) => {
        const description = removeLeadingTrailingBrTags(row.description || '');
        const isValidDescription = description && description !== '<br>';
        return isValidDescription ? 150 : 0;
      }}
      sx={{
        ...dataGridStyle,
        ...(isSmallScreen && {
          '& .MuiDataGrid-columnHeader[data-field="doi"], & .MuiDataGrid-columnHeader[data-field="publicationDoi"]': {
            px: 0.5,
          },
          '& .MuiDataGrid-columnHeader[data-field="doi"] .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-columnHeader[data-field="publicationDoi"] .MuiDataGrid-columnHeaderTitle':
            {
              overflow: 'visible',
              textOverflow: 'clip',
            },
          '& .MuiDataGrid-cell[data-field="doi"], & .MuiDataGrid-cell[data-field="publicationDoi"]': {
            px: 0.5,
            justifyContent: 'center',
          },
        }),
      }}
    />
  );
};

// Helper functions
function formatAvailableData(nbSamples: number, nbActivities: number): string {
  const samples = nbSamples > 0 ? `${nbSamples} Samples` : '';
  const activities = nbActivities > 0 ? 'Theory Data' : '';
  return `${samples}${samples && activities ? ' + ' : ''}${activities}`;
}

function formatType(isSnapshot: boolean, status: string, isPublic: boolean): string {
  const snapshot = isSnapshot ? 'Snapshot' : 'Live';
  const published = isPublic ? ' - Published' : status === StatusDto.Pending ? ' - Pending' : '';
  return snapshot + published;
}
